import React from "react";
import { CalendarDaysIcon, ClockIcon } from "@heroicons/react/24/outline";
import CampaignThumb from "./CampaignThumb";
import {
  CAMPAIGN_STATUS_BADGE,
  displayCreativeName,
  formatDays,
  formatPlayDuration,
} from "./campaignFormatting";
import { COVERAGE_STATUSES } from "../../utils/coverageMap";
import { formatDisplayDate, formatDisplayDateTime } from "../../utils/dateUtils";
import type {
  CampaignStatus,
  MapCampaign,
  MapSlot,
} from "../../schemas/campaignSchema";

const STATUS_LABEL: Record<CampaignStatus, string> = COVERAGE_STATUSES.reduce(
  (acc, s) => ({ ...acc, [s.status]: s.label }),
  {} as Record<CampaignStatus, string>,
);

/** Without a count: this one thing is on air. With one: how many are. */
export const OnAirPill: React.FC<{ count?: number }> = ({ count }) => (
  <span className="inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
    {count === undefined ? "On air" : `${count} on air`}
  </span>
);

/** "11:00–20:00, every day" — when in the day this runs. */
export const slotHours = (slot: MapSlot): string => {
  const times =
    slot.startTime && slot.endTime
      ? `${slot.startTime.slice(0, 5)}–${slot.endTime.slice(0, 5)}`
      : "All day";
  const days = formatDays(slot.daysOfWeek);
  return days === "Every day" ? `${times}, every day` : `${times}, ${days}`;
};

/** "21 Sep – 30 Sep 2026" — over what dates it runs. */
export const slotDates = (slot: MapSlot): string => {
  if (!slot.startDate && !slot.endDate) return "No end date";
  const from = slot.startDate ? formatDisplayDate(slot.startDate) : "—";
  const to = slot.endDate ? formatDisplayDate(slot.endDate) : "—";
  // Both ends in the same year: say the year once, at the end.
  const year = from.slice(-4);
  return year === to.slice(-4)
    ? `${from.slice(0, -5)} – ${to}`
    : `${from} – ${to}`;
};

/**
 * "21 Sep, 15:44". The year is noise for something that played this year, and
 * this sits in a row of numbers meant to be read at a glance.
 */
export const lastPlayed = (iso?: string | null): string => {
  if (!iso) return "Never";
  const thisYear = new Date().getFullYear();
  return formatDisplayDateTime(iso).replace(` ${thisYear},`, ",");
};

/**
 * Slots that share a date range, as one range with its hours under it. Two
 * slots over the same weeks (say every day plus a weekday top-up) are one line
 * of dates, not the same dates printed twice.
 */
export const groupSlotsByDates = (
  slots: MapSlot[],
): { dates: string; hours: string[] }[] => {
  const groups = new Map<string, string[]>();
  slots.forEach((slot) => {
    const dates = slotDates(slot);
    groups.set(dates, [...(groups.get(dates) ?? []), slotHours(slot)]);
  });
  return [...groups.entries()].map(([dates, hours]) => ({ dates, hours }));
};

/** The schedule as date ranges, each with its hours: calendar, then clocks. */
export const SlotSchedule: React.FC<{ slots: MapSlot[] }> = ({ slots }) => (
  <ul className="flex flex-col gap-1.5">
    {groupSlotsByDates(slots).map(({ dates, hours }) => (
      <li key={dates} className="text-xs text-gray-600">
        <span className="flex items-center gap-1.5 font-medium text-gray-700">
          <CalendarDaysIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
          {dates}
        </span>
        {hours.map((h, i) => (
          <span key={i} className="mt-0.5 flex items-center gap-1.5 pl-0.5">
            <ClockIcon className="h-3 w-3 flex-shrink-0 text-gray-400" />
            {h}
          </span>
        ))}
      </li>
    ))}
  </ul>
);

/** One number with its label under it, so the row scans as numbers. */
const Metric: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div>
    <div className="text-sm font-semibold text-gray-900">{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-gray-400">
      {label}
    </div>
  </div>
);

/**
 * One campaign as it runs on one screen — the bottom of both drill-downs, so
 * the map panel and the list accordion say the same thing the same way.
 *
 * Built to be read at a glance rather than line by line: preview, name, status,
 * then the numbers with their labels under them, then the schedule on its own
 * line behind an icon so it reads as "when" rather than as more statistics.
 */
const CampaignRunCard: React.FC<{ campaign: MapCampaign }> = ({ campaign }) => {
  const badge = CAMPAIGN_STATUS_BADGE[campaign.status];

  return (
    <li className="flex gap-3 rounded-lg border border-gray-200 bg-white p-3">
      <CampaignThumb thumbnailUrl={campaign.thumbnailUrl} className="h-12 w-20" />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span
            // The stored name is the uploaded filename; the title keeps it.
            title={campaign.name}
            className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900"
          >
            {displayCreativeName(campaign.name)}
          </span>
          <span
            className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
          >
            {STATUS_LABEL[campaign.status]}
          </span>
          {campaign.isPlayingNow && <OnAirPill />}
        </div>

        <div className="mt-2 flex flex-wrap items-start gap-x-6 gap-y-2">
          <Metric value={campaign.plays.toLocaleString()} label="plays" />
          <Metric
            value={formatPlayDuration(campaign.playDuration)}
            label="on this screen"
          />
          <Metric value={lastPlayed(campaign.lastPlayedAt)} label="last played" />
        </div>

        <div className="mt-2 border-t border-gray-100 pt-2">
          <SlotSchedule slots={campaign.slots} />
        </div>
      </div>
    </li>
  );
};

export default CampaignRunCard;
